import { useState } from "react";
import { Bot, UserMinus } from "lucide-react";
import { ModalShell } from "@/components/ModalShell";
import { EmployeePicker } from "@/components/EmployeePicker";
import { useConfirm } from "@/components/useConfirm";
import { displayName, personInitials, type Person } from "@/lib/board/types";

/**
 * Who is on this board. Everyone can see the list. Admins, the owner, and
 * holders of "Manage board access" can add and remove people. The database
 * enforces the same rule.
 *
 * Removing someone takes them off this board's cards as well (the cards stay),
 * so a card is never left assigned to a person who can no longer open it.
 */
export function MembersPanel({
  members, everyone, ownerId, openCardsFor, canManage, openToAll = false, onAdd, onRemove, onClose,
}: {
  members: Person[];
  /** Active humans and AI teammates, to pick new members from. */
  everyone: Person[];
  ownerId: string | null;
  /** The board is already open to every active employee. Membership is a list, not the door. */
  openToAll?: boolean;
  openCardsFor: (id: string) => number;
  canManage: boolean;
  onAdd: (id: string) => Promise<void>;
  onRemove: (id: string) => Promise<number>;
  onClose: () => void;
}) {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();

  const memberIds = new Set(members.map(m => m.id));
  const candidates = everyone.filter(p => !memberIds.has(p.id));

  async function add() {
    if (!pick) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      await onAdd(pick);
      const who = everyone.find(p => p.id === pick);
      const name = who ? displayName(who) : "They";
      setNote(openToAll ? `${name} is on the member list. They could already open this board.` : `${name} can open the board now.`);
      setPick("");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: Person) {
    const open = openCardsFor(p.id);
    const ok = await ask({
      title: `Remove ${displayName(p)} from this board?`,
      body: openToAll
        ? (open > 0
          ? `${displayName(p)} can still open this board, because it is open to every active employee. They will be taken off ${open} open card${open === 1 ? "" : "s"}.`
          : `${displayName(p)} can still open this board. This only takes them off the member list.`)
        : (open > 0
          ? `${displayName(p)} has ${open} open card${open === 1 ? "" : "s"} here. They will be taken off ${open === 1 ? "it" : "them"}, and the cards stay on the board.`
          : "They will no longer be able to open this board."),
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      const n = await onRemove(p.id);
      setNote(`${displayName(p)} removed${n ? `, and taken off ${n} card${n === 1 ? "" : "s"}` : ""}.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Board members (${members.length})`} onClose={onClose} dismissable={!busy}>
      <div className="space-y-4">
        {openToAll && (
          <p className="text-sm text-gray2">
            Every active employee can already open this board, including people hired later.
            The list below is who was added by name. Taking someone off it does not lock them out while the board stays open.
          </p>
        )}
        {err && <div role="alert" className="rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{err}</div>}
        {note && <div role="status" className="rounded-lg bg-ok-bg px-3 py-2 text-sm text-ok">{note}</div>}

        <ul className="divide-y divide-line rounded-lg ring-1 ring-line">
          {members.map(p => (
            <li key={p.id} className="flex items-center gap-3 px-3 py-2">
              <span
                aria-hidden
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  p.employee_kind === "ai" ? "bg-ink text-white" : "bg-teal-mint text-teal-dark"
                }`}
              >
                {personInitials(p)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 truncate text-sm font-medium">
                  {displayName(p)}
                  {p.employee_kind === "ai" && <Bot size={12} className="text-gray2" aria-label="AI teammate" />}
                </div>
                <div className="text-xs text-gray2">
                  {p.id === ownerId ? "Owner" : `${openCardsFor(p.id)} open card${openCardsFor(p.id) === 1 ? "" : "s"}`}
                </div>
              </div>
              {canManage && p.id !== ownerId && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void remove(p)}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-bad hover:bg-bad-bg disabled:opacity-50"
                  aria-label={`Remove ${displayName(p)} from this board`}
                >
                  <UserMinus size={14} aria-hidden /> Remove
                </button>
              )}
            </li>
          ))}
        </ul>

        {canManage ? (
          <div className="flex flex-wrap items-end gap-2">
            <EmployeePicker
              className="min-w-0 flex-1"
              label="Add someone"
              placeholder="Choose a person or AI teammate…"
              people={candidates}
              value={pick}
              onChange={setPick}
              disabled={busy}
            />
            <button type="button" className="btn" disabled={!pick || busy} onClick={() => void add()}>
              Add to board
            </button>
          </div>
        ) : (
          <p className="text-xs text-gray2">Ask an admin or the board owner to add or remove people.</p>
        )}
      </div>
      {dialog}
    </ModalShell>
  );
}
